import type { GroupPricingStructure } from '../../api/group/groupAdminApiSlice';

/**
 * Pure rules of the "Räume zusammenlegen" dialog (plan
 * `room-houses-and-combining-rooms.md` §4 row 4). The server decides —
 * `assessCombinability` — and this only shapes the choice it is asked about,
 * so a refusal is rare rather than impossible.
 */

/**
 * Who is offered "Raum teilen" and "Räume zusammenlegen". Not the page's
 * owner-only `readOnly`: both reshape inventory, so they follow the inventory
 * right `create:assets` — the one the endpoints require
 * (`new-backend/admin/src/routes/inventoryRoutes.ts`), held by owner and
 * manager (PO, 2026-10-13: managers combine and divide rooms). A master keeps
 * the page's rule: editable only on a tenant they own.
 */
export const canReshapeRooms = (
    { readOnly, isMaster, hasCreateAssets }: { readOnly: boolean, isMaster: boolean, hasCreateAssets: boolean },
): boolean => (isMaster ? !readOnly : hasCreateAssets);

export const MIN_PARTS = 2;
export const MAX_PARTS = 3;

/**
 * The rooms that may be offered as parts: other rooms of the same use case at
 * the SAME location (one house) — the server's location rule, applied to the
 * list so the operator is not offered a room it will refuse. A room without a
 * Product cannot be named and is left out, as on the divide action.
 */
const sameLocationRooms = (
    whole: GroupPricingStructure,
    groups: GroupPricingStructure[],
): GroupPricingStructure[] => groups.filter((group) => group.productId
    && group.productId !== whole.productId
    && group.locationId !== null
    && group.locationId === whole.locationId);

/** Products reached through more than one room (cell) — several rooms, one price. */
const sharedProductIds = (groups: GroupPricingStructure[]): Set<string> => {
    const seen: Record<string, number> = {};
    groups.forEach((group) => {
        if (group.productId) seen[group.productId] = (seen[group.productId] ?? 0) + 1;
    });
    return new Set(Object.keys(seen).filter((id) => (seen[id] ?? 0) > 1));
};

/**
 * ...and of those, only rooms with a Product of their own. A part must be a
 * single room — the server's `not_a_single_room` — and a Product several rooms
 * share for their price is not one. Offering it gave one checkbox per room
 * under one Product id, ticking together and always refused.
 */
export const combineCandidates = (
    whole: GroupPricingStructure,
    groups: GroupPricingStructure[],
): GroupPricingStructure[] => {
    const shared = sharedProductIds(groups);
    return sameLocationRooms(whole, groups).filter((group) => !shared.has(group.productId as string));
};

/** The rooms at the location left out because they share a Product — named, not hidden. */
export const sharedProductRooms = (
    whole: GroupPricingStructure,
    groups: GroupPricingStructure[],
): GroupPricingStructure[] => {
    const shared = sharedProductIds(groups);
    return sameLocationRooms(whole, groups).filter((group) => shared.has(group.productId as string));
};

/** Why the selection cannot be sent yet, or null. */
export const selectionProblem = (selected: string[]): string | null => {
    if (selected.length < MIN_PARTS) return 'services.combine_rooms.validation.too_few';
    if (selected.length > MAX_PARTS) return 'services.combine_rooms.validation.too_many';
    return null;
};

/** The parts' capacities summed — the suggestion, as on divide. '' when unknown. */
export const suggestCombinedCapacity = (capacities: Array<number | null | undefined>): string => {
    const known = capacities.filter((value): value is number => typeof value === 'number' && value > 0);
    return known.length === capacities.length && known.length > 0
        ? String(known.reduce((sum, value) => sum + value, 0))
        : '';
};

const REFUSALS = new Set([
    'unsupported_tenant', 'part_count', 'duplicate_part', 'part_is_whole',
    'unsupported_use_case', 'not_a_single_room', 'already_composite', 'part_of_composite',
    'whole_has_bookings', 'part_pooled', 'different_location', 'location_mismatch',
    'whole_capacity_below_part', 'room_has_lock',
]);

/** The i18n key for a server refusal, or null for an unknown reason. */
export const combineRefusalKey = (reason: string | null | undefined): string | null => (
    reason && REFUSALS.has(reason) ? `services.combine_rooms.refusals.${reason}` : null
);
